import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, CheckCircle2, Clock, Loader2, Trophy, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { fetchScorePredictionMatches, submitScorePrediction } from '../../services/scorePredictions';

const formatKickoff = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
    timeZoneName: 'short',
  });
};

const LOCK_BEFORE_KICKOFF_MS = 60 * 60 * 1000;

const toDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getLockDate = (match) => {
  const lockDate = toDate(match?.lock_at);
  if (lockDate) return lockDate;
  const kickoffDate = toDate(match?.kickoff_at);
  return kickoffDate ? new Date(kickoffDate.getTime() - LOCK_BEFORE_KICKOFF_MS) : null;
};

const formatCountdown = (targetDate, nowMs) => {
  if (!targetDate) return '-';
  const remaining = Math.max(0, targetDate.getTime() - nowMs);
  if (remaining <= 0) return 'Now';
  const totalSeconds = Math.floor(remaining / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const predictionKey = (match) => `${match?.prediction?.home_score ?? ''}-${match?.prediction?.away_score ?? ''}`;

export default function ScorePredictionsPage() {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const homePath = tenantSlug ? `/${tenantSlug}/app` : '/app';
  const eventPath = tenantSlug ? `/${tenantSlug}/app/fifa-kit` : '/app/fifa-kit';
  const [matches, setMatches] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [nowMs, setNowMs] = useState(() => Date.now());

  const hydrateDrafts = useCallback((items) => {
    const next = {};
    items.forEach((match) => {
      next[match.id] = {
        home_score: match.prediction?.home_score ?? '',
        away_score: match.prediction?.away_score ?? '',
        predictionKey: predictionKey(match),
      };
    });
    setDrafts(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const items = await fetchScorePredictionMatches();
      setMatches(items);
      hydrateDrafts(items);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load score predictions');
    } finally {
      setLoading(false);
    }
  }, [hydrateDrafts]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const groupedMatches = useMemo(() => matches.reduce((acc, match) => {
    const key = (match.kickoff_at || '').slice(0, 10) || 'Matches';
    if (!acc[key]) acc[key] = [];
    acc[key].push(match);
    return acc;
  }, {}), [matches]);

  const updateDraft = (matchId, key, value) => {
    setDrafts((prev) => ({
      ...prev,
      [matchId]: {
        ...(prev[matchId] || {}),
        [key]: value,
      },
    }));
  };

  const savePrediction = async (match) => {
    const draft = drafts[match.id] || {};
    if (draft.home_score === '' || draft.away_score === '') {
      toast.error('Enter both scores');
      return;
    }
    setSavingId(match.id);
    try {
      const result = await submitScorePrediction(match.id, draft.home_score, draft.away_score);
      const nextItem = result.item;
      setMatches((prev) => prev.map((row) => row.id === match.id ? nextItem : row));
      setDrafts((prev) => ({
        ...prev,
        [match.id]: {
          home_score: nextItem.prediction?.home_score ?? '',
          away_score: nextItem.prediction?.away_score ?? '',
          predictionKey: predictionKey(nextItem),
        },
      }));
      toast.success(match.prediction ? 'Prediction updated' : 'Prediction saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not save prediction');
    } finally {
      setSavingId('');
    }
  };

  return (
    <div className="player-app min-h-screen pb-12" data-testid="score-predictions-page">
      <div className="px-4 sm:px-6 py-4 flex items-center gap-3" style={{ background: '#000000' }}>
        <button
          onClick={() => navigate(eventPath)}
          className="w-10 h-10 rounded-full flex items-center justify-center transition active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            color: '#FFFFFF',
          }}
          data-testid="score-predictions-back"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="display flex items-center gap-2 text-xl sm:text-2xl font-extrabold text-white">
            <Trophy className="w-6 h-6" style={{ color: '#FFD93D' }} />
            Predict the Score
          </h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
            Exact score wins Cups. Edit until 1 hour before kickoff.
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex min-h-[260px] items-center justify-center text-white/60">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading matches...
          </div>
        ) : matches.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 text-center">
            <Trophy className="mx-auto h-10 w-10 text-white/30" />
            <p className="mt-3 font-bold text-white">No score predictions open yet</p>
            <p className="mt-1 text-sm text-white/55">Matches appear 48 hours before kickoff.</p>
            <button
              type="button"
              onClick={() => navigate(homePath)}
              className="mt-5 rounded-2xl px-5 py-3 text-sm font-black"
              style={{ background: '#FFD93D', color: '#3F2A00' }}
            >
              Back Home
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedMatches).map(([dateKey, rows]) => (
              <section key={dateKey} className="space-y-3">
                <p className="display text-xs font-extrabold uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  {formatKickoff(rows[0]?.kickoff_at).split(',').slice(0, 2).join(',')}
                </p>
                <div className="space-y-3">
                  {rows.map((match) => (
                    <MatchCard
                      key={match.id}
                      match={match}
                      draft={drafts[match.id] || {}}
                      saving={savingId === match.id}
                      nowMs={nowMs}
                      onDraft={updateDraft}
                      onSave={savePrediction}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function MatchCard({ match, draft, saving, nowMs, onDraft, onSave }) {
  const prediction = match.prediction;
  const kickoffDate = toDate(match.kickoff_at);
  const lockDate = getLockDate(match);
  const kickoffPassed = Boolean(kickoffDate && nowMs >= kickoffDate.getTime());
  const lockPassed = Boolean(lockDate && nowMs >= lockDate.getTime());
  const locked = !match.can_predict || lockPassed;
  const hasPrediction = Boolean(prediction);
  const dirty = `${draft.home_score ?? ''}-${draft.away_score ?? ''}` !== (draft.predictionKey ?? '-');
  const resultKnown = match.result_home_score !== null && match.result_home_score !== undefined
    && match.result_away_score !== null && match.result_away_score !== undefined;
  const won = prediction?.status === 'won';
  const lost = prediction?.status === 'lost';
  const completed = match.status === 'completed' || won || lost;
  const finalScoreText = resultKnown
    ? `${match.home_team} ${match.result_home_score} - ${match.result_away_score} ${match.away_team}`
    : '';
  const finalScorePrefix = finalScoreText ? `Final score: ${finalScoreText}. ` : '';
  const matchStatusValue = completed ? 'Completed' : (kickoffPassed ? 'Started' : formatCountdown(kickoffDate, nowMs));
  const matchStatusTone = completed ? 'green' : (kickoffPassed ? 'neutral' : 'blue');

  return (
    <div
      className="rounded-3xl border p-4 sm:p-5"
      style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.065), rgba(245,215,110,0.055) 50%, rgba(34,211,238,0.045))',
        borderColor: won ? 'rgba(34,197,94,0.55)' : lost ? 'rgba(248,113,113,0.45)' : 'rgba(255,255,255,0.14)',
        boxShadow: '0 16px 38px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.08)',
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="rounded-lg px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em]" style={{ background: 'rgba(255,217,61,0.14)', color: '#FFD93D' }}>
          Group {match.group}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white/65">
          <Clock className="h-3 w-3" />
          {completed ? 'Completed' : locked ? 'Locked' : 'Open'}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <TeamName name={match.home_team} align="right" />
        <span className="display text-sm font-black text-white/45">VS</span>
        <TeamName name={match.away_team} />
      </div>

      <p className="mt-3 text-center text-xs text-white/55">{formatKickoff(match.kickoff_at)}</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <CountdownPill
          label={kickoffPassed ? 'Match status' : 'Match starts in'}
          value={matchStatusValue}
          tone={matchStatusTone}
        />
        <CountdownPill
          label={lockPassed ? 'Prediction lock' : 'Prediction locks in'}
          value={lockPassed ? 'Locked' : formatCountdown(lockDate, nowMs)}
          tone={lockPassed ? 'yellow' : 'green'}
        />
      </div>
      {Number(match.reward_amount || 0) > 0 && (
        <p className="mt-2 text-center text-xs font-bold text-[#FFD93D]">
          Exact score reward: {Number(match.reward_amount).toLocaleString()} Cups
        </p>
      )}
      {resultKnown && (
        <div className="mt-3 rounded-2xl border border-white/10 bg-black/25 px-3 py-2 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-white/45">Final score</p>
          <p className="display mt-0.5 text-sm font-black text-white">
            {finalScoreText}
          </p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <ScoreInput
          value={draft.home_score ?? ''}
          disabled={locked || won || lost || saving}
          onChange={(value) => onDraft(match.id, 'home_score', value)}
        />
        <span className="display text-xl font-black text-white/50">-</span>
        <ScoreInput
          value={draft.away_score ?? ''}
          disabled={locked || won || lost || saving}
          onChange={(value) => onDraft(match.id, 'away_score', value)}
        />
      </div>

      <div className="mt-4">
        {won ? (
          <Message icon={CheckCircle2} color="#22C55E" text={`${finalScorePrefix}You won ${Number(prediction.reward_amount || 0).toLocaleString()} Cups`} />
        ) : lost ? (
          <Message icon={XCircle} color="#F87171" text={`${finalScorePrefix}Prediction wrong. Better try next time.`} />
        ) : resultKnown && hasPrediction ? (
          <Message icon={Clock} color="#A7F3D0" text={`${finalScorePrefix}Reward distribution pending.`} />
        ) : locked && hasPrediction ? (
          <Message icon={Clock} color="#FDE68A" text={`Your prediction is locked: ${prediction.home_score}-${prediction.away_score}`} />
        ) : locked ? (
          <Message icon={Clock} color="#FDE68A" text="Prediction window closed for this match." />
        ) : hasPrediction ? (
          <Message icon={CheckCircle2} color="#A7F3D0" text={`Saved prediction: ${prediction.home_score}-${prediction.away_score}. You can edit before lock.`} />
        ) : (
          <Message icon={Trophy} color="#FFD93D" text="Enter exact final score before the lock time." />
        )}
      </div>

      {!won && !lost && !locked && (
        <button
          type="button"
          onClick={() => onSave(match)}
          disabled={saving || (!dirty && hasPrediction)}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-black transition active:scale-[0.99] disabled:opacity-50"
          style={{ background: '#FFD93D', color: '#3F2A00' }}
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          {hasPrediction ? 'Update Prediction' : 'Save Prediction'}
        </button>
      )}
    </div>
  );
}

function TeamName({ name, align = 'left' }) {
  return (
    <p className={`display min-w-0 text-base sm:text-lg font-extrabold leading-tight text-white ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {name}
    </p>
  );
}

function ScoreInput({ value, disabled, onChange }) {
  return (
    <input
      type="number"
      min="0"
      step="1"
      inputMode="numeric"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      className="h-14 w-full rounded-2xl border border-white/10 bg-black/35 px-3 text-center text-2xl font-black text-white outline-none focus:border-[#FFD93D] disabled:opacity-60"
      placeholder="0"
    />
  );
}

function Message({ icon: Icon, color, text }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-black/25 px-3 py-2 text-center text-xs font-bold" style={{ color }}>
      <Icon className="h-4 w-4 shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function CountdownPill({ label, value, tone = 'blue' }) {
  const tones = {
    blue: {
      border: 'rgba(96,165,250,0.32)',
      bg: 'rgba(37,99,235,0.12)',
      label: 'rgba(191,219,254,0.82)',
      value: '#BFDBFE',
    },
    green: {
      border: 'rgba(52,211,153,0.32)',
      bg: 'rgba(16,185,129,0.10)',
      label: 'rgba(167,243,208,0.82)',
      value: '#A7F3D0',
    },
    yellow: {
      border: 'rgba(253,230,138,0.30)',
      bg: 'rgba(245,158,11,0.10)',
      label: 'rgba(253,230,138,0.82)',
      value: '#FDE68A',
    },
    neutral: {
      border: 'rgba(255,255,255,0.14)',
      bg: 'rgba(255,255,255,0.05)',
      label: 'rgba(255,255,255,0.56)',
      value: 'rgba(255,255,255,0.82)',
    },
  };
  const current = tones[tone] || tones.blue;
  return (
    <div
      className="rounded-2xl border px-3 py-2 text-center"
      style={{
        background: current.bg,
        borderColor: current.border,
      }}
    >
      <p className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: current.label }}>
        {label}
      </p>
      <p className="display mt-0.5 text-sm font-extrabold tabular-nums" style={{ color: current.value }}>
        {value}
      </p>
    </div>
  );
}
